import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[5]

export const KWP_5_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-5-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-5',
  title: 'Instalacja fotowoltaiczna 5 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 5 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla mniejszych gospodarstw domowych.',
  keywords: [
    'instalacja fotowoltaiczna 5 kWp cena',
    'panele słoneczne 5 kWp koszt',
    'ile kosztuje instalacja 5 kWp',
    '5 kWp instalacja fotowoltaiczna dom',
  ],
  eyebrow: 'Wielkość instalacji: 5 kWp',
  h1: 'Instalacja fotowoltaiczna 5 kWp: cena i wartości orientacyjne',
  intro:
    'Instalacja 5 kWp to typowa wielkość startowa dla mniejszych gospodarstw domowych bez pompy ' +
    'ciepła i samochodu elektrycznego. Znajdziesz tu wartości orientacyjne dotyczące powierzchni ' +
    'dachu i liczby paneli, rozbicie kosztów, a tuż poniżej kalkulator z już wypełnioną średnią ' +
    'ceną dla 5 kWp, obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 5 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 5 kWp zwykle pasuje do gospodarstwa 2-3-osobowego z podstawowym ' +
        'zużyciem ok. 3 500-4 000 kWh rocznie, bez pompy ciepła i samochodu elektrycznego. Jeśli w ' +
        'przyszłości dojdzie dodatkowy odbiornik prądu, lepiej od razu rozważyć większą instalację, ' +
        'np. 8 lub 10 kWp, zamiast później ją rozbudowywać.',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 5 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: zamienia wytworzony prąd stały na użyteczny prąd zmienny',
        'Montaż/robocizna: mocowanie na dachu, okablowanie',
        'Rusztowanie: zależnie od wysokości i dostępności dachu',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, podłączenie do sieci domowej',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Przy mniejszych instalacjach, takich jak 5 kWp, zgłoszenie i podstawowe rusztowanie ' +
        'stanowią proporcjonalnie większą część całkowitego kosztu niż przy większych instalacjach, ' +
        'ponieważ te pozycje nie rosną proporcjonalnie do wielkości instalacji. Dlatego cena za kWp ' +
        'przy 5 kWp jest zwykle nieco wyższa niż przy 10 czy 15 kWp.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        'Bez magazynu energii udział autokonsumpcji w instalacji 5 kWp wynosi zwykle ok. 25-35 %, a ' +
        'z odpowiednio dobranym magazynem energii wyraźnie więcej. To, jak szybko instalacja się ' +
        'zamortyzuje, zależy od Twojego profilu zużycia, aktualnej taryfy za nadwyżki energii oraz ' +
        'ewentualnych dofinansowań. Dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator ' +
        'doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Czy instalacja 5 kWp wystarczy dla domu jednorodzinnego?',
      a: 'Dla mniejszych gospodarstw domowych bez pompy ciepła i samochodu elektrycznego zwykle tak. Przy wyższym lub rosnącym zużyciu lepiej od razu wybrać większą wielkość, np. 8 lub 10 kWp, zamiast rozbudowywać instalację później.',
    },
    {
      q: 'Czy przy 5 kWp opłaca się jeszcze magazyn energii?',
      a: 'Tak, choć dobrany w mniejszym rozmiarze niż przy większych instalacjach. To, czy się opłaca, zależy głównie od tego, ile prądu zużywasz w ciągu dnia - firmy rzemieślnicze mogą to ocenić na podstawie Twojego profilu zużycia.',
    },
    {
      q: 'Ile paneli potrzebuję do 5 kWp?',
      a: `Wartość orientacyjna: ok. ${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp, w zależności od wybranego modelu może być nieco więcej lub mniej.`,
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 8 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-8-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-12-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
